import { useDialog } from "../components/DialogProvider";
import { ActionStatus } from "../components/ActionStatus";
import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  DEFAULT_CONFIG,
  getAppConfig,
  listRecordings,
  listRooms,
  type RoomListItem,
} from "../api/recordings";
import { ApiError } from "../api/client";
import { listTags } from "../api/tags";
import type {
  AppConfig,
  ListResponse,
  Recording,
  RecordingStatus,
  RecordingTag,
  SnippetInfo,
  SnippetSpan,
} from "../types";
import { UploadModal } from "../upload/UploadModal";
import {
  abortFromState,
  dropResumeState,
  listResumableUploads,
  type ResumeState,
} from "../upload/MultipartUploader";
import { hasGroup, useAuth } from "../auth/AuthContext";

const PAGE_SIZE = 25;

/** Группы Authentik, которым разрешена загрузка файлов (viewer — нельзя). */
const MANAGER_GROUP = "recordings-manager";
const ADMIN_GROUP = "recordings-admin";

// Период тихого polling'а списка в фоне (мс). 10 сек — компромисс между
// «свежестью» (новая запись/прогресс-бар двигается, статусы прокликиваются)
// и нагрузкой на recorder-api. При document.hidden polling приостанавливается.
const POLL_INTERVAL_MS = 10_000;

export default function RecordingsList() {
  const { confirm, alert } = useDialog();
  // Фильтры в URL (useSearchParams) — чтобы при «Назад» из RecordingView
  // браузер восстановил тот же список с теми же фильтрами/пагинацией.
  // setSearchParams со {replace: true}: не засоряем history каждым typeingом;
  // переход на /r/:id — обычный navigate, который добавит entry поверх.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const roomFilter = searchParams.get("room") ?? "";
  const sourceFilter = searchParams.get("source") ?? ""; // "" | "element_call" | "upload"
  const fromDate = searchParams.get("from") ?? "";
  const toDate = searchParams.get("to") ?? "";
  const visibility = searchParams.get("visibility") ?? "";
  const tagFilter = searchParams.getAll("tag").join(",");
  const page = Math.max(0, parseInt(searchParams.get("page") ?? "0", 10) || 0);

  // Локальный mirror search-инпута — типизация мгновенная, в URL пишем через
  // debounce. Иначе каждый удар по клавиатуре менял бы URL → polling reset.
  const [searchInput, setSearchInput] = useState(search);
  // Внешнее изменение URL q (например через browser back) синкаем в input.
  // Сравниваем со значением search из URL — useEffect стрельнёт только при
  // фактической смене.
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Helper: атомарное обновление searchParams с replace, чтобы не засорять
  // history. mutate получает копию текущих params, должен изменить in-place.
  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Debounced push search input → URL ?q=
  useEffect(() => {
    if (searchInput === search) return; // no-op если уже синхронизированы
    const t = setTimeout(() => {
      updateParams((p) => {
        if (searchInput) p.set("q", searchInput);
        else p.delete("q");
        p.delete("page"); // смена запроса → возврат на первую страницу
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search, updateParams]);

  // Сеттеры для остальных фильтров пишут в URL сразу (без debounce —
  // select/date input не дёргают на каждый символ).
  const setRoomFilter = useCallback(
    (v: string) =>
      updateParams((p) => {
        if (v) p.set("room", v);
        else p.delete("room");
        p.delete("page");
      }),
    [updateParams],
  );
  const setSourceFilter = useCallback(
    (v: string) =>
      updateParams((p) => {
        if (v) p.set("source", v);
        else p.delete("source");
        p.delete("page");
      }),
    [updateParams],
  );
  const setFromDate = useCallback(
    (v: string) =>
      updateParams((p) => {
        if (v) p.set("from", v);
        else p.delete("from");
        p.delete("page");
      }),
    [updateParams],
  );
  const setToDate = useCallback(
    (v: string) =>
      updateParams((p) => {
        if (v) p.set("to", v);
        else p.delete("to");
        p.delete("page");
      }),
    [updateParams],
  );
  const setPage = useCallback(
    (v: number) =>
      updateParams((p) => {
        if (v > 0) p.set("page", String(v));
        else p.delete("page");
      }),
    [updateParams],
  );

  // Данные
  const [rooms, setRooms] = useState<RoomListItem[]>([]);
  const [tags, setTags] = useState<RecordingTag[]>([]);
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // loading=true только для пользовательских контекст-смен (initial mount,
  // смена страницы, смена фильтров). Background polling каждые 10s не
  // трогает loading — обновление происходит «тихо», без спиннера.
  const [loading, setLoading] = useState(true);
  const [abortingId, setAbortingId] = useState<string | null>(null);
  const abortLock = useRef<string | null>(null);
  // Конфиг от бэка (HEAVY_RTF, OVERHEAD) — для прогресс-бара транскрибации.
  // Читаем один раз при mount; при ошибке используем DEFAULT_CONFIG.
  const [appConfig, setAppConfig] = useState<AppConfig>(DEFAULT_CONFIG);

  // Загрузка доступна только manager/admin (viewer — нет).
  const { user } = useAuth();
  const canUpload = hasGroup(user, MANAGER_GROUP) || hasGroup(user, ADMIN_GROUP) || hasGroup(user, "recordings-supermanager");

  // Upload UI state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [resumeContext, setResumeContext] = useState<
    { state: ResumeState; file: File } | null
  >(null);
  const [resumableUploads, setResumableUploads] = useState<ResumeState[]>([]);
  const resumeFileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingResumeStateRef = useRef<ResumeState | null>(null);
  // Trigger перезагрузки списка после успешного upload'а.
  const [refreshTick, setRefreshTick] = useState(0);

  // На mount читаем localStorage — есть ли незавершённые upload'ы.
  useEffect(() => {
    setResumableUploads(listResumableUploads());
  }, []);

  // Debounce search и сброс page реализованы выше через updateParams. Здесь
  // больше нет отдельных эффектов на эти задачи.

  // Загрузка списка комнат — один раз
  useEffect(() => {
    listRooms()
      .then((res) => setRooms(res.rooms))
      .catch(() => {
        // Не критично — оставляем dropdown пустым
      });
  }, []);
  useEffect(() => { void listTags().then(setTags).catch(() => setTags([])); }, [refreshTick]);

  // Загрузка конфига (HEAVY_RTF, OVERHEAD) — один раз при mount.
  // getAppConfig() сам делает fallback на DEFAULT_CONFIG при ошибке,
  // так что catch здесь не нужен.
  useEffect(() => {
    getAppConfig().then(setAppConfig);
  }, []);

  // Единая функция загрузки страницы. silent=true — тихий background
  // polling: не показываем спиннер, не сбрасываем error на success
  // (чтобы не «мерцать» если временный 500 пройдёт и вернётся).
  const fetchPage = useCallback(
    async (silent: boolean) => {
      if (!silent) setLoading(true);
      try {
        const res = await listRecordings({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          q: search || undefined,
          room_id: roomFilter || undefined,
          source: sourceFilter || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          visibility: visibility || undefined,
          tags: tagFilter ? tagFilter.split(",") : undefined,
        });
        setData(res);
        setError(null);
      } catch (err: unknown) {
        if (silent) {
          // Не шумим в UI на временные сбои polling'а — следующий тик
          // попробует ещё раз. Логнём в консоль для диагностики.
          // eslint-disable-next-line no-console
          console.warn("[polling] list failed (silent):", err);
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setError(
            "Доступ запрещён. У вашего аккаунта нет группы recordings-viewer или recordings-manager.",
          );
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [search, roomFilter, sourceFilter, fromDate, toDate, page, visibility, tagFilter],
  );

  // Контекст-смена (фильтры, пагинация, upload-trigger) — со спиннером.
  useEffect(() => {
    fetchPage(false);
  }, [fetchPage, refreshTick]);

  // Тихий polling в фоне каждые POLL_INTERVAL_MS. Пауза когда таб скрыт
  // (Page Visibility API) — на возврат немедленно фетчим и резюмим интервал.
  // Если есть открытый upload-модал — не polling'уем, чтобы не «перетряхнуть»
  // resume-state и не мешать пользователю.
  useEffect(() => {
    if (showUploadModal) return;
    let intervalId: number | null = null;
    const start = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        void fetchPage(true);
      }, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Возврат фокуса — сначала immediate fetch (UI мог сильно отстать),
        // потом резюм интервала.
        void fetchPage(true);
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchPage, showUploadModal]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const canPrev = page > 0;
  const canNext = !!data && page < totalPages - 1;

  return (
    <div className="page">
      <h1>
        Записи
        {data && (
          <span className="muted recordings-count">
            ({data.total})
          </span>
        )}
        {canUpload && (
          <span className="header-actions">
            <button
              type="button"
              className="page-button primary"
              onClick={() => setShowUploadModal(true)}
            >
              📤 Загрузить файл
            </button>
          </span>
        )}
      </h1>

      {/* Скрытый input для resume-flow: юзер кликает "Продолжить" в баннере
          → программно открываем file picker → onChange валидирует и
          стартует upload. */}
      <input
        ref={resumeFileInputRef}
        type="file"
        className="is-hidden"
        accept=".mp4,.webm,.mov,.mkv,.avi,.m4v,.mp3,.m4a,.wav,.ogg,.opus,.flac"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          const pending = pendingResumeStateRef.current;
          if (!f || !pending) return;
          if (f.name !== pending.filename || f.size !== pending.size_bytes) {
            void alert({ title: "Файл не совпадает", description: `Ожидался "${pending.filename}" (${pending.size_bytes} B), выбрано "${f.name}" (${f.size} B).` });
            return;
          }
          setResumeContext({ state: pending, file: f });
          pendingResumeStateRef.current = null;
          setShowUploadModal(true);
        }}
      />

      {canUpload &&
        resumableUploads.length > 0 &&
        resumableUploads.map((rs) => {
          const uploadedBytes = rs.completed_parts.length * rs.part_size;
          const pct = Math.min(
            100,
            Math.round((uploadedBytes / Math.max(1, rs.size_bytes)) * 100),
          );
          return (
            <div key={rs.recording_id} className="resume-banner">
              <span>
                Незавершённая загрузка: <strong>{rs.filename}</strong>{" "}
                <span className="muted">
                  (~{pct}% — {rs.completed_parts.length} ч.)
                </span>
              </span>
              <span className="resume-banner-actions">
                <button
                  type="button"
                  className="page-button primary"
                  disabled={abortingId === rs.recording_id}
                  onClick={() => {
                    pendingResumeStateRef.current = rs;
                    resumeFileInputRef.current?.click();
                  }}
                >
                  Продолжить
                </button>
                <button
                  type="button"
                  className="page-button"
                  disabled={abortingId === rs.recording_id}
                  onClick={async () => {
                    if (abortLock.current) return;
                    abortLock.current = rs.recording_id;
                    try {
                      if (
                        !await confirm({ title: "Отменить загрузку?", description: `Загрузка «${rs.filename}» будет отменена, уже загруженные части удалятся.`, confirmLabel: "Отменить загрузку", cancelLabel: "Продолжить загрузку", danger: true })
                      ) {
                        return;
                      }
                      setAbortingId(rs.recording_id);
                      await abortFromState(rs);
                      setResumableUploads((prev) =>
                        prev.filter((s) => s.recording_id !== rs.recording_id),
                      );
                    } finally {
                      abortLock.current = null;
                      setAbortingId(null);
                    }
                  }}
                >
                  Отменить
                </button>
                {abortingId === rs.recording_id && <ActionStatus>Отменяем загрузку…</ActionStatus>}
              </span>
            </div>
          );
        })}

      <div className="recordings-filters">
      <FilterBar
        search={searchInput}
        onSearchChange={setSearchInput}
        roomFilter={roomFilter}
        onRoomChange={setRoomFilter}
        sourceFilter={sourceFilter}
        onSourceChange={setSourceFilter}
        fromDate={fromDate}
        onFromChange={setFromDate}
        toDate={toDate}
        onToChange={setToDate}
        rooms={rooms}
      />

      <div className="recordings-filter-extra">
        <label className="recordings-filter-field"><span>Доступ</span><select className="filter-select" value={visibility} onChange={e => updateParams(p => { if (e.target.value) p.set("visibility", e.target.value); else p.delete("visibility"); p.delete("page"); })}><option value="">Все режимы</option><option value="restricted">Ограниченный</option><option value="staff">Сотрудникам NAN</option><option value="confidential">Конфиденциальная</option></select></label>
        <TagFilter tags={tags} selected={searchParams.getAll("tag")} onChange={values => updateParams(p => { p.delete("tag"); values.forEach(value => p.append("tag", value)); p.delete("page"); })} />
        {(search || roomFilter || sourceFilter || fromDate || toDate || visibility || tagFilter) && <button type="button" className="filter-reset" onClick={() => { setSearchInput(""); updateParams(p => { ["q", "room", "source", "from", "to", "tag", "visibility", "page"].forEach(key => p.delete(key)); }); }}>Сбросить фильтры</button>}
      </div>
      </div>

      {error && <p className="error">{error}</p>}

      {!error && (
        <>
          <RecordingsTable
            recordings={data?.recordings ?? []}
            loading={loading}
            appConfig={appConfig}
            filterSearch={searchParams.toString()}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            canPrev={canPrev}
            canNext={canNext}
            onPrev={() => setPage(Math.max(0, page - 1))}
            onNext={() => setPage(page + 1)}
            total={data?.total ?? 0}
            pageSize={PAGE_SIZE}
          />
        </>
      )}

      {showUploadModal && (
        <UploadModal
          resumeState={resumeContext?.state}
          resumeFile={resumeContext?.file}
          onClose={() => {
            setShowUploadModal(false);
            setResumeContext(null);
            setResumableUploads(listResumableUploads());
          }}
          onSuccess={(recordingId) => {
            dropResumeState(recordingId);
            setResumableUploads(listResumableUploads());
            // Перезагрузить список — новая запись должна появиться.
            setRefreshTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

function FilterBar({
  search,
  onSearchChange,
  roomFilter,
  onRoomChange,
  sourceFilter,
  onSourceChange,
  fromDate,
  onFromChange,
  toDate,
  onToChange,
  rooms,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  roomFilter: string;
  onRoomChange: (v: string) => void;
  sourceFilter: string;
  onSourceChange: (v: string) => void;
  fromDate: string;
  onFromChange: (v: string) => void;
  toDate: string;
  onToChange: (v: string) => void;
  rooms: RoomListItem[];
}) {
  // Сортируем dropdown по имени комнаты (или ID если имя пустое).
  const sortedRooms = useMemo(() => {
    return [...rooms].sort((a, b) => {
      const aName = a.display_name || a.id;
      const bName = b.display_name || b.id;
      return aName.localeCompare(bName);
    });
  }, [rooms]);

  return <div className="recordings-filter-main">
    <label className="recordings-filter-field recordings-filter-search"><span>Поиск</span><input type="search" placeholder="Название, текст записи…" aria-label="Поиск по записям" value={search} onChange={e => onSearchChange(e.target.value)} className="filter-input" /></label>
    <label className="recordings-filter-field"><span>Тип</span><select value={sourceFilter} onChange={e => onSourceChange(e.target.value)} className="filter-select"><option value="">Все типы</option><option value="element_call">Звонки</option><option value="upload">Загрузки</option></select></label>
    <label className="recordings-filter-field"><span>Комната</span><select value={roomFilter} onChange={e => onRoomChange(e.target.value)} className="filter-select"><option value="">Все комнаты</option>{sortedRooms.map(room => <option key={room.id} value={room.id}>{room.display_name || room.id}</option>)}</select></label>
    <label className="recordings-filter-field"><span>Дата с</span><input type="date" value={fromDate} max={toDate || undefined} onChange={e => onFromChange(e.target.value)} className="filter-input filter-date" /></label>
    <label className="recordings-filter-field"><span>Дата по</span><input type="date" value={toDate} min={fromDate || undefined} onChange={e => onToChange(e.target.value)} className="filter-input filter-date" /></label>
  </div>;
}

function TagFilter({ tags, selected, onChange }: { tags: RecordingTag[]; selected: string[]; onChange: (values: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener("pointerdown", outside); document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [open]);
  const names = selected.map(id => tags.find(t => t.id === id)?.name || "Метка недоступна");
  const label = names.length === 0 ? "Все метки" : names.length <= 2 ? names.join(", ") : `Выбрано: ${names.length}`;
  return <div className="recordings-filter-field filter-tags" ref={ref}><span>Метки</span><button ref={buttonRef} type="button" className="filter-tags-trigger" aria-label={`Фильтр по меткам: ${label}`} aria-expanded={open} aria-controls="filter-tags-menu" onClick={() => setOpen(!open)}><span>{label}</span><span aria-hidden="true">⌄</span></button>
    {open && <div className="filter-tags-menu" id="filter-tags-menu" role="group" aria-label="Фильтр по меткам">{tags.length ? tags.map(tag => <label key={tag.id}><input type="checkbox" checked={selected.includes(tag.id)} onChange={e => onChange(e.target.checked ? [...selected, tag.id] : selected.filter(id => id !== tag.id))} /><span>{tag.name}</span></label>) : <p>Нет доступных меток</p>}{selected.length > 0 && <button type="button" onClick={() => onChange([])}>Сбросить метки</button>}</div>}
  </div>;
}

function RecordingsTable({
  recordings,
  loading,
  appConfig,
  filterSearch,
}: {
  recordings: Recording[];
  loading: boolean;
  appConfig: AppConfig;
  /** Сериализованные URL search params списка (q/room/source/from/to/page).
   *  Прокидываются в URL записи при клике, чтобы кнопка "← К списку" на
   *  странице записи смогла вернуть пользователя ровно к тем же
   *  результатам поиска. Пустая строка — фильтры не активны. */
  filterSearch: string;
}) {
  if (loading && recordings.length === 0) {
    return <p className="muted">Загрузка…</p>;
  }
  if (recordings.length === 0) {
    return <p className="muted">Ничего не найдено по этим фильтрам.</p>;
  }
  return (
    <table className={"recordings-table" + (loading ? " is-loading" : "")}>
      <thead>
        <tr>
          <th>Дата</th>
          <th>Название</th>
          <th>Комната</th>
          <th>Кто начал</th>
          <th className="col-num">Длит.</th>
          <th>Статус</th>
          <th className="col-num">Участников</th>
        </tr>
      </thead>
      <tbody>
        {recordings.map((rec) => (
          <RecordingRow
            key={rec.id}
            rec={rec}
            appConfig={appConfig}
            filterSearch={filterSearch}
          />
        ))}
      </tbody>
    </table>
  );
}

/** Кол-во колонок таблицы — для colspan в expandable снippet-rows. Если
 *  поменяешь шапку выше, обнови и здесь. */
const TABLE_COLSPAN = 7;

/**
 * MatchBadge — компактный бейдж рядом с title в карточке результата поиска.
 * Показывает откуда пришло совпадение. Несколько источников могут быть
 * одновременно (запись матчит и по названию, и по транскрипту).
 *
 * "title_or_summary" не дублируем визуально — само название уже видно.
 * Рендерим только "transcript" и "room_name" — для них не очевидно где матч.
 */
function MatchBadges({ sources }: { sources?: string[] }) {
  if (!sources || sources.length === 0) return null;
  const items: { key: string; icon: string; label: string }[] = [];
  if (sources.includes("transcript"))
    items.push({ key: "transcript", icon: "📝", label: "в транскрипте" });
  if (sources.includes("room_name"))
    items.push({ key: "room_name", icon: "🏷", label: "в названии комнаты" });
  if (items.length === 0) return null;
  return (
    <span className="match-badges">
      {items.map((b) => (
        <span key={b.key} className="match-badge" title={b.label}>
          {b.icon}
        </span>
      ))}
    </span>
  );
}

/** Подсветка matchнутых spans внутри сниппета. <mark> по флагу highlight. */
function SnippetText({ spans }: { spans: SnippetSpan[] }) {
  return (
    <>
      {spans.map((s, i) =>
        s.highlight ? (
          <mark key={i}>{s.text}</mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
    </>
  );
}

/** mm:ss из миллисекунд для UI-кнопки jump-to-time. */
function formatMs(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * SnippetRows — раскрытие сниппетов под основной строкой записи. Контракт:
 *   - Если snippets пуст → ничего не рендерим.
 *   - Если ровно один → всегда показан, без кнопки "ещё".
 *   - Если N>1 → первый виден всегда, остальные скрыты под кнопкой "Ещё N-1".
 *     По клику разворачиваются, кнопка превращается в "Свернуть".
 * Каждый сниппет — отдельная <tr> с colspan, клик уводит на /r/:id?ms=start.
 * Чтобы клик НЕ всплыл до RecordingRow (там тоже handleClick), используем
 * stopPropagation.
 */
function SnippetRows({
  snippets,
  buildRecordingURL,
}: {
  snippets: SnippetInfo[];
  /** Фабрика URL для записи — наследует все filter-params списка (q/room/
   *  source/from/to/page), плюс добавляет переданные extras. */
  buildRecordingURL: (extras?: Record<string, string>) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  if (snippets.length === 0) return null;
  const visible = expanded ? snippets : snippets.slice(0, 1);
  const remaining = snippets.length - 1;

  const goToTime = (e: MouseEvent<HTMLElement>, startMs: number) => {
    e.stopPropagation();
    // Добавляем ms к текущим filter-params — URL записи получит и q=
    // (для будущей подсветки в полном транскрипте), и room/source/...
    // чтобы "← К списку" из RecordingView вернул на ту же страницу поиска.
    navigate(buildRecordingURL({ ms: String(startMs) }));
  };

  return (
    <>
      {visible.map((sn) => (
        <tr
          key={sn.seq}
          className="snippet-row"
          onClick={(e) => goToTime(e, sn.start_ms)}
        >
          <td colSpan={TABLE_COLSPAN} className="snippet-cell">
            <button
              type="button"
              className="snippet-time"
              onClick={(e) => goToTime(e, sn.start_ms)}
              aria-label={`Перейти к ${formatMs(sn.start_ms)}`}
            >
              ▶ {formatMs(sn.start_ms)}
            </button>
            <span className="snippet-text">
              <SnippetText spans={sn.spans} />
            </span>
          </td>
        </tr>
      ))}
      {snippets.length > 1 && (
        <tr className="snippet-row snippet-row--toggle">
          <td colSpan={TABLE_COLSPAN} className="snippet-cell">
            <button
              type="button"
              className="link-button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((x) => !x);
              }}
            >
              {expanded ? "Свернуть" : `Ещё ${remaining}`}
            </button>
          </td>
        </tr>
      )}
    </>
  );
}

function RecordingRow({
  rec,
  appConfig,
  filterSearch,
}: {
  rec: Recording;
  appConfig: AppConfig;
  filterSearch: string;
}) {
  const navigate = useNavigate();
  /** Помощник для построения URL записи с пробросом текущих filter params.
   *  Опциональный extras — дополнительные параметры (например, ms= для
   *  jump-to-time из сниппета). */
  const buildRecordingURL = (extras?: Record<string, string>): string => {
    const params = new URLSearchParams(filterSearch);
    if (extras) {
      for (const [k, v] of Object.entries(extras)) {
        params.set(k, v);
      }
    }
    const qs = params.toString();
    return qs ? `/r/${rec.id}?${qs}` : `/r/${rec.id}`;
  };
  const startedAt = new Date(rec.started_at);
  const dateStr =
    startedAt.toLocaleDateString() +
    " " +
    startedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const duration = rec.duration_ms ? formatDuration(rec.duration_ms) : "—";
  const isUpload = rec.source === "upload";
  // Для upload'а в колонке "Комната" показываем "Загрузка: <filename>".
  // Для звонка — room_display_name (snapshot имени/иерархии Spaces).
  const contextName = isUpload
    ? rec.original_filename || rec.room_display_name || "Загруженный файл"
    : rec.room_display_name ?? rec.room_id;
  const title = rec.title?.trim() ?? "";
  // Счётчик «👤 N»:
  //   - звонок: длина acl_users (кто был в комнате) — известна с момента
  //     finalize, валидна на любом статусе.
  //   - upload + готовый статус (transcribed / no_speech): speaker_count
  //     от pyannote — реальное число голосов в записи. Но если запись
  //     транскрибирована БЕЗ диаризации (ретрай в лёгком режиме) или это
  //     no_speech — speaker_count = 0/undefined: показываем "—", а не "0"
  //     (голоса были, мы их просто не считали — "0" вводит в заблуждение).
  //   - upload + промежуточный статус (uploading/assembled/transcribing/...):
  //     speaker_count ещё не посчитан → "—", т.к. acl_users у upload'а
  //     это только владелец, что не отражает количество говорящих.
  const participantDisplay: string | number = isUpload
    ? (rec.status === "transcribed" || rec.status === "no_speech") &&
      (rec.speaker_count ?? 0) > 0
      ? rec.speaker_count!
      : "—"
    : rec.acl_users?.length ?? 0;

  // Клик по строке = переход на запись. НО если юзер сейчас что-то выделил
  // (например протянул мышкой по тексту, чтобы скопировать) — не переходим.
  // click событие срабатывает после mouseup, и в этот момент Selection API
  // уже содержит выделенный диапазон. Также игнорим клики с зажатым
  // ctrl/meta/shift — это пользователь хочет open in new tab или multi-select.
  const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
    const sel = window.getSelection();
    if (sel && sel.toString().length > 0) return;
    navigate(buildRecordingURL());
  };

  return (
    <>
      <tr className="recordings-row" onClick={handleClick}>
        <td className="muted">{dateStr}</td>
        <td>
          <span
            className="source-badge"
            title={isUpload ? "Загрузка" : "Запись звонка"}
          >
            {isUpload ? "📎" : "📞"}
          </span>
          {title ? (
            <strong>{title}</strong>
          ) : (
            <span className="muted cell-untitled">
              {rec.status === "no_speech" ? "без речи" : "без названия"}
            </span>
          )}
          <MatchBadges sources={rec.match_sources} />
          <span className={`badge-access visibility-badge visibility-badge--${rec.visibility === "confidential" ? "confidential" : rec.visibility === "staff" ? "staff" : "restricted"}`}>
            {rec.visibility === "confidential" ? "Конфиденциальная" : rec.visibility === "staff" ? "Сотрудникам NAN" : "Ограниченный доступ"}
          </span>
          {rec.tags?.map(tag => <span className="badge-access recording-list-tag" key={tag.id} style={tag.color ? {borderColor: tag.color} : undefined}>{tag.color && <span className="recording-list-tag-dot" style={{backgroundColor: tag.color}} aria-hidden="true" />}{tag.name}</span>)}
        </td>
        <td className="muted">{contextName}</td>
        <td className="muted">{rec.started_by}</td>
        <td className="col-num">{duration}</td>
        <td>
          <StatusBadge status={rec.status} />
          {rec.status === "transcribing" && (
            <TranscribeProgress rec={rec} appConfig={appConfig} />
          )}
        </td>
        <td className="col-num">{participantDisplay}</td>
      </tr>
      {rec.snippets && rec.snippets.length > 0 && (
        <SnippetRows
          snippets={rec.snippets}
          buildRecordingURL={buildRecordingURL}
        />
      )}
    </>
  );
}

function Pagination({
  page,
  totalPages,
  canPrev,
  canNext,
  onPrev,
  onNext,
  total,
  pageSize,
}: {
  page: number;
  totalPages: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  total: number;
  pageSize: number;
}) {
  if (total === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="pagination">
      <span className="muted">
        {from}–{to} из {total}
      </span>
      <div className="pagination-buttons">
        <button
          type="button"
          className="page-button"
          disabled={!canPrev}
          onClick={onPrev}
        >
          ← Назад
        </button>
        <span className="muted page-of">
          Стр. {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="page-button"
          disabled={!canNext}
          onClick={onNext}
        >
          Вперёд →
        </button>
      </div>
    </div>
  );
}

// Человекочитаемые подписи статусов. Если статус не в map — рендерим как
// есть (для дев-удобства новые статусы видны явно, а не теряются).
// finalized и assembled оба обозначают «обработка после звонка», но это
// разные стадии pipeline: finalized = звонок завершён, чанки в S3, ждёт
// сборки (recorder-assembly); assembled = сборка готова, ждёт claim'а
// transcribe-воркером. Чтобы пользователь видел продвижение —
// разделяем лейблы (и цвета через CSS status-finalized vs status-assembled).
const STATUS_LABELS: Partial<Record<RecordingStatus, string>> = {
  recording: "идёт запись",
  recoverable: "ожидает докачки с устройства",
  uploading: "загрузка",
  finalized: "ожидает сборки",
  assembled: "ожидает транскрибации",
  transcribing: "транскрибация",
  transcribed: "готово",
  no_speech: "без речи",
  failed: "ошибка",
};

function StatusBadge({ status }: { status: RecordingStatus }) {
  const label = STATUS_LABELS[status] ?? status;
  return <span className={`status status-${status}`}>{label}</span>;
}

/**
 * TranscribeProgress — прогресс-бар + текст оставшегося времени для записей
 * в статусе 'transcribing'. Полностью клиентский: фронт берёт
 * transcribe_started_at из Recording, длительность аудио (duration_ms) и
 * формулу (HEAVY_RTF, OVERHEAD) из AppConfig, считает прогресс локально
 * раз в секунду по wall-clock'у.
 *
 *   ETA_total_sec = duration_sec × HEAVY_RTF + OVERHEAD
 *   % = (now - transcribe_started_at) / ETA_total_sec
 *
 * Если transcribe_started_at или duration_ms отсутствуют — возвращаем null
 * (бар не рисуем). 100% не выставляем никогда: статус сменится на
 * transcribed/no_speech через polling, тогда бар уйдёт сам. Cap на 99%.
 */
function TranscribeProgress({
  rec,
  appConfig,
}: {
  rec: Recording;
  appConfig: AppConfig;
}) {
  // Тикер раз в секунду — пересчёт % и оставшегося времени. Render-only,
  // не дёргает API.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Defensive: если данные не пришли (старый бэк без поля или duration
  // нулевая) — ничего не рисуем. Бар без duration лжив, лучше скрыть.
  if (!rec.transcribe_started_at || !rec.duration_ms || rec.duration_ms <= 0) {
    return null;
  }
  const startedAtMs = new Date(rec.transcribe_started_at).getTime();
  if (!Number.isFinite(startedAtMs)) return null;

  const elapsedSec = Math.max(0, (nowMs - startedAtMs) / 1000);
  const durationSec = rec.duration_ms / 1000;
  const etaTotalSec =
    durationSec * appConfig.transcribe_heavy_rtf + appConfig.transcribe_overhead_sec;
  if (etaTotalSec <= 0) return null;

  const rawPct = (elapsedSec / etaTotalSec) * 100;
  const pct = Math.max(0, Math.min(99, rawPct));
  const remainingSec = Math.max(0, etaTotalSec - elapsedSec);
  const isOverdue = rawPct >= 99;

  return (
    <div className="transcribe-progress" aria-label="прогресс транскрибации">
      <div className="transcribe-progress-bar">
        <div
          className="transcribe-progress-fill"
          style={{ width: `${pct.toFixed(1)}%` }}
        />
      </div>
      <span className="transcribe-progress-label muted">
        {isOverdue ? "дольше обычного…" : `осталось ~${formatRemaining(remainingSec)}`}
      </span>
    </div>
  );
}

/**
 * formatRemaining — компактное отображение оставшегося времени.
 * < 60 сек → "Xс". 1-60 мин → "Mм". ≥ 1 час → "Hч Mм".
 * Секунды для длинных записей бессмысленны (формула приблизительная).
 */
function formatRemaining(sec: number): string {
  if (sec < 60) return `${Math.max(0, Math.round(sec))}с`;
  const totalMin = Math.round(sec / 60);
  if (totalMin < 60) return `${totalMin}м`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}ч` : `${h}ч ${m}м`;
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }
  return `${m}:${s.toString().padStart(2, "0")}`;
}
